import {
  type CollisionDetection,
  closestCorners,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import React from 'react';
import { KanbanColumn } from './KanbanColumn';

export interface ColumnData {
  id: UniqueIdentifier;
  title: React.ReactNode;
}

export interface ItemData {
  id: UniqueIdentifier;
  columnId: UniqueIdentifier;
  content: React.ReactNode;
}

interface KanbanBoardProps {
  columns: ColumnData[];
  items: ItemData[];
  onDragEnd: (event: DragEndEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
  onDragStart?: (event: DragStartEvent) => void;
  activeItemId?: UniqueIdentifier | null;
}

export function KanbanBoard({
  columns,
  items,
  onDragEnd,
  onDragOver,
  onDragStart,
  activeItemId,
}: KanbanBoardProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const [internalActiveId, setInternalActiveId] = React.useState<UniqueIdentifier | null>(null);
  const [boardItems, setBoardItems] = React.useState<ItemData[]>(items);
  const isDraggingRef = React.useRef(false);
  const initialItemsRef = React.useRef<ItemData[]>(items);

  // Synchronize boardItems with incoming items prop when not actively dragging
  React.useEffect(() => {
    if (!isDraggingRef.current) {
      setBoardItems(items);
    }
  }, [items]);

  const handleDragStart = (event: DragStartEvent) => {
    isDraggingRef.current = true;
    initialItemsRef.current = boardItems;
    setInternalActiveId(event.active.id);
    if (onDragStart) onDragStart(event);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    setBoardItems((prevItems) => {
      const activeItem = prevItems.find((i) => i.id === activeId);
      if (!activeItem) return prevItems;

      const overItem = prevItems.find((i) => i.id === overId);
      const overColumn = columns.find((c) => c.id === overId);

      const activeColumnId = activeItem.columnId;
      const overColumnId = overItem ? overItem.columnId : overColumn ? overColumn.id : null;

      if (!overColumnId) return prevItems;

      if (activeColumnId !== overColumnId) {
        // Cross-column movement: re-parent item to destination column
        const overColumnItems = prevItems.filter(
          (i) => i.columnId === overColumnId && i.id !== activeId
        );
        let newIndex = overColumnItems.length;

        if (overItem) {
          const overItemIndex = overColumnItems.findIndex((i) => i.id === overId);
          if (overItemIndex >= 0) {
            const isBelowOverItem =
              active.rect.current.translated &&
              over.rect &&
              active.rect.current.translated.top > over.rect.top + over.rect.height / 2;
            newIndex = isBelowOverItem ? overItemIndex + 1 : overItemIndex;
          }
        }

        newIndex = Math.max(0, Math.min(newIndex, overColumnItems.length));

        const nextItems = prevItems.filter((i) => i.id !== activeId);
        const updatedActiveItem: ItemData = { ...activeItem, columnId: overColumnId };

        if (overColumnItems.length === 0 || newIndex >= overColumnItems.length) {
          const lastItemIndex = nextItems.map((i) => i.columnId).lastIndexOf(overColumnId);
          if (lastItemIndex !== -1) {
            nextItems.splice(lastItemIndex + 1, 0, updatedActiveItem);
          } else {
            nextItems.push(updatedActiveItem);
          }
        } else {
          const targetItem = overColumnItems[newIndex];
          const targetIndex = nextItems.findIndex((i) => i.id === targetItem.id);
          nextItems.splice(targetIndex >= 0 ? targetIndex : nextItems.length, 0, updatedActiveItem);
        }

        return nextItems;
      }

      // Same column reordering
      if (overItem && activeId !== overId) {
        const oldIndex = prevItems.findIndex((i) => i.id === activeId);
        const newIndex = prevItems.findIndex((i) => i.id === overId);
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          return arrayMove(prevItems, oldIndex, newIndex);
        }
      }

      return prevItems;
    });

    if (onDragOver) onDragOver(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    isDraggingRef.current = false;
    setInternalActiveId(null);

    const { active, over } = event;
    if (!over) {
      setBoardItems(initialItemsRef.current);
      return;
    }

    const activeItem = boardItems.find((i) => i.id === active.id);
    const finalColumnId = activeItem?.columnId;

    if (onDragEnd) {
      onDragEnd({
        ...event,
        over: over
          ? {
              ...over,
              data: {
                ...over.data,
                current: {
                  ...over.data.current,
                  columnId: finalColumnId,
                },
              },
            }
          : null,
      });
    }
  };

  const handleDragCancel = () => {
    isDraggingRef.current = false;
    setInternalActiveId(null);
    setBoardItems(initialItemsRef.current);
  };

  const collisionDetectionStrategy: CollisionDetection = React.useCallback((args) => {
    // 1. Pointer-within check: prioritize item collisions over column backgrounds
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      const itemCollision = pointerCollisions.find(
        (c) =>
          (c.data?.droppableContainer?.data?.current as Record<string, unknown> | undefined)
            ?.type === 'Item'
      );
      if (itemCollision) {
        return [itemCollision];
      }
      return pointerCollisions;
    }

    // 2. Fallback to closest corners for smooth dragging between cards or into empty columns
    const cornersCollisions = closestCorners(args);
    if (cornersCollisions.length > 0) {
      const itemCollision = cornersCollisions.find(
        (c) =>
          (c.data?.droppableContainer?.data?.current as Record<string, unknown> | undefined)
            ?.type === 'Item'
      );
      if (itemCollision) {
        return [itemCollision];
      }
      return cornersCollisions;
    }

    return [];
  }, []);

  const activeId = activeItemId !== undefined ? activeItemId : internalActiveId;
  const activeItem = activeId
    ? boardItems.find((item) => item.id === activeId) ||
      initialItemsRef.current.find((item) => item.id === activeId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="h-full w-full overflow-x-auto">
        <div className="mx-auto flex h-full w-max min-w-full justify-center gap-4 p-4">
          {columns.map((col) => {
            const colItems = boardItems.filter((item) => item.columnId === col.id);
            return <KanbanColumn key={col.id} column={col} items={colItems} />;
          })}
        </div>
      </div>
      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <div className="opacity-95 rotate-1 scale-[1.02] shadow-2xl cursor-grabbing rounded-xl ring-2 ring-primary/20 pointer-events-none">
            {activeItem.content}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
